import React, {useMemo} from 'react';
import {getPersonData, getTimeData, getProjectData, UpdateApprovalStatus, getNoApprovalStatusChangeMessage} from './TimeApprovalLogic';
import {APPROVAL_TYPE, APPROVAL_STATUS} from '../../../constants';
import {AssigneeGroup, HexagonText, Table, ProjectIndicator, Button, CheckmarkIcon, CrossIcon, EmailIcon, TimesheetsIcon} from 'web-components';
import Util from '../../../forecast-app/shared/util/util';
import {TableWrapper, TimesheetsIconWrapper} from './TimeApprovalPage_styled';
import NotesPopOut from '../../../forecast-app/shared/components/popups/notes_pop_out';
import {useHistory} from 'react-router-dom';
import {ProjectIndicatorWrapper} from '../../../containers/modal/time_approval/ActivateTimeApprovalModal_styled';
import ForecastTooltip from '../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import TooltipContainer from '../../../forecast-app/shared/components/tooltips/tooltip_container';

const TimeApprovalTable = ({sectionType, onRemind, currentViewingDate, enabledColumns, intl, isGroupedByProject, firstGroupData}) => {
	const secondGroupData = datas => {
		return isGroupedByProject ? getPersonData(datas.project.timeRegs, datas.project.id) : getProjectData(datas.person.timeRegs, datas.person.id);
	};

	const baselineData = rowData => {
		return getTimeData(isGroupedByProject ? rowData.person.timeRegs : rowData.project.timeRegs);
	};

	const data = {rows: firstGroupData};

	const history = useHistory();
	const showTaskModal = taskId => {
		Util.showTaskModal(taskId, history);
	};

	const buttonValidation = useMemo(() => {
		switch (sectionType) {
			case APPROVAL_STATUS.SUBMITTED:
				return {action: APPROVAL_TYPE.APPROVE, text: intl.formatMessage({id: 'project_budget.approve_expense_button'})};
			default:
				return {action: null, text: ''};
		}
	}, [sectionType]);

	const buttonReminder = useMemo(() => {
		switch (sectionType) {
			case APPROVAL_STATUS.NOT_SUBMITTED:
				return {action: APPROVAL_TYPE.SUBMIT, text: intl.formatMessage({id: 'time_approval.submit'})};
			default:
				return {action: null, text: ''};
		}
	});
	const buttonRejection = useMemo(() => {
		switch (sectionType) {
			case APPROVAL_STATUS.SUBMITTED:
				return {action: APPROVAL_TYPE.UNSUBMIT, text: intl.formatMessage({id: 'time_approval.unsubmit'})};
			case APPROVAL_STATUS.APPROVED:
				return {action: APPROVAL_TYPE.UNAPPROVE, text: intl.formatMessage({id: 'time_approval.unapprove'})};
			default:
				return {action: null, text: ''};
		}
	}, [sectionType]);

	const onClickHandler = (e, personId, projectId, isValidationButton, noApprovalStatusChange, person) => {
		e.stopPropagation();
		if (noApprovalStatusChange) return;
		if (isValidationButton) {
			UpdateApprovalStatus(personId && [personId], projectId && [projectId], buttonValidation.action, currentViewingDate, intl, person);
		} else {
			UpdateApprovalStatus(personId && [personId], projectId && [projectId], buttonRejection.action, currentViewingDate, intl, person);
		}
	};

	const timesheetsRedirect = (e, personId) => {
		e.stopPropagation();
		const firstDayOfWeekFormatted = currentViewingDate.format('YYYY-MM-DD');
		window.open(`/timesheets?personId=${personId}&date=${firstDayOfWeekFormatted}`);
	};

	const handleRemind = person => {
		onRemind(person);
	};

	const weekHeaderSection = () => {
		return Array(7)
			.fill(0)
			.map((d, index) => {
				const day = currentViewingDate.clone().add(index, 'day');
				return (
					<Table.HeaderColumn align="right" visible={true} width="SMALL" bordered>
						<Table.HeaderColumn.Title>
							{day.format('ddd')} <br /> {day.format('D MMM')}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				);
			});
	};

	const weekRowsColumns = data => {
		return Array(7)
			.fill(0)
			.map((d, index) => {
				const total = data.totals[index];
				return <Table.Column usePadding>{total != null ? Util.convertMinutesToFullHour(data.totals[index], intl, true) : '-'}</Table.Column>;
			});
	};

	const totalsColumn = total => {
		return <Table.Column usePadding>{total != null ? Util.convertMinutesToFullHour(total, intl, true) : '-'}</Table.Column>;
	};

	const projectRow = rowData => {
		const noApprovalStatusChange = rowData.project.noApprovalStatusChange;
		return [
			<Table.Column>
				<ProjectIndicatorWrapper>
					<ProjectIndicator name={rowData.project.name} id={rowData.project.id} project={rowData.project} showProjectName={true}></ProjectIndicator>
					<span>{rowData.project.name}</span>
				</ProjectIndicatorWrapper>
			</Table.Column>,
			<Table.Column>
				<div style={{marginLeft: '16px'}}>
					<AssigneeGroup showCount={4} assignees={rowData.project.projectOwner} />
				</div>
			</Table.Column>,
			<Table.Column></Table.Column>,
			<Table.Column></Table.Column>,
			weekRowsColumns(rowData.project),
			<Table.Column></Table.Column>,
			totalsColumn(rowData.project.total),
			<Table.Column>
				{buttonRejection.action &&
					(isGroupedByProject ? (
						//fake button to keep thet flex space evenly of the column and keep the approve button on the right
						<div style={{width: '32px'}}></div>
					) : (
						<ForecastTooltip content={noApprovalStatusChange ? getNoApprovalStatusChangeMessage(noApprovalStatusChange, intl.formatMessage) : intl.formatMessage({id: 'approval.reject'})}>
							<Button
								isSquare
								variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE}
								iconPosition={Button.ICON_POSITION.FIRST}
								icon={() => <CrossIcon size={CrossIcon.SIZE.SMALL} color={noApprovalStatusChange ? '#A1A1A1' : '#F40000'} />}
								disabled={noApprovalStatusChange}
								onClick={e => onClickHandler(e, rowData.project.parentId, rowData.project.id, false, noApprovalStatusChange, !!rowData.project.parentId ? rowData.project.timeRegs[0].person : null)}
								cy={'project-reject-button'}
							></Button>
						</ForecastTooltip>
					))}
				{buttonValidation.action && (
					<ForecastTooltip content={noApprovalStatusChange ? getNoApprovalStatusChangeMessage(noApprovalStatusChange, intl.formatMessage) : intl.formatMessage({id: 'approval.approve'})}>
						<Button
							isSquare
							variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE}
							iconPosition={Button.ICON_POSITION.FIRST}
							icon={() => <CheckmarkIcon />}
							disabled={noApprovalStatusChange}
							onClick={e => onClickHandler(e, rowData.project.parentId, rowData.project.id, true, noApprovalStatusChange, !!rowData.project.parentId ? rowData.project.timeRegs[0].person : null)}
							cy={'project-approve-button'}
						></Button>
					</ForecastTooltip>
				)}
			</Table.Column>,
		];
	};

	const personRow = rowData => {
		const noApprovalStatusChange = rowData.person.noApprovalStatusChange;
		return [
			<Table.Column>
				<div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
					<div style={{minWidth: '0px'}}>
						<HexagonText key={rowData.person.id} text={`${rowData.person.firstName} ${rowData.person.lastName}`} imgUrl={rowData.person.profilePictureId} />
					</div>
					<TooltipContainer infoText={intl.formatMessage({id: 'timesheet.go_to_persons_timesheet'}, {firstName: rowData.person.firstName})} growable>
						<TimesheetsIconWrapper className="row-hover">
							<TimesheetsIcon onClick={e => timesheetsRedirect(e, rowData.person.id)} />
						</TimesheetsIconWrapper>
					</TooltipContainer>
				</div>
			</Table.Column>,
			<Table.Column></Table.Column>,
			<Table.Column></Table.Column>,
			<Table.Column></Table.Column>,
			weekRowsColumns(rowData.person),
			<Table.Column></Table.Column>,
			totalsColumn(rowData.person.total),
			<Table.Column>
				<Table.Column.Cell onClick={() => false}>
					<div style={{display: 'flex', justifyContent: 'space-evenly', width: '100px'}}>
						{buttonRejection.action && (
							<ForecastTooltip content={noApprovalStatusChange ? getNoApprovalStatusChangeMessage(noApprovalStatusChange, intl.formatMessage) : intl.formatMessage({id: 'approval.reject'})}>
								<Button
									isSquare
									variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE}
									iconPosition={Button.ICON_POSITION.FIRST}
									icon={() => <CrossIcon color={noApprovalStatusChange ? '#A1A1A1' : '#F40000'} size={CrossIcon.SIZE.SMALL} />}
									disabled={noApprovalStatusChange}
									onClick={e => onClickHandler(e, rowData.person.id, rowData.person.parentId, false, noApprovalStatusChange, rowData.person)}
									cy={'person-reject-button'}
								></Button>
							</ForecastTooltip>
						)}
						{buttonValidation.action && (
							<ForecastTooltip content={noApprovalStatusChange ? getNoApprovalStatusChangeMessage(noApprovalStatusChange, intl.formatMessage) : intl.formatMessage({id: 'approval.approve'})}>
								<Button
									isSquare
									variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE}
									iconPosition={Button.ICON_POSITION.FIRST}
									icon={() => <CheckmarkIcon />}
									disabled={noApprovalStatusChange}
									onClick={e => onClickHandler(e, rowData.person.id, rowData.person.parentId, true, noApprovalStatusChange, rowData.person)}
									cy={'person-approve-button'}
								></Button>
							</ForecastTooltip>
						)}
						{buttonReminder.action && (
							<Button
								isSquare
								variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE}
								iconPosition={Button.ICON_POSITION.FIRST}
								icon={() => <EmailIcon />}
								disabled={noApprovalStatusChange}
								onClick={() => handleRemind(rowData.person)}
							></Button>
						)}
					</div>
				</Table.Column.Cell>
			</Table.Column>,
		];
	};

	const taskTimeRow = rowData => {
		return [
			<Table.Column>
				<Table.Column.Clickable onClickHandler={() => showTaskModal(rowData.task.taskId)}>{rowData.task.name}</Table.Column.Clickable>
			</Table.Column>,
			<Table.Column></Table.Column>,
			<Table.Column>{Util.convertMinutesToFullHour(rowData.task.estimated, intl, true)}</Table.Column>,
			<Table.Column>{Util.convertMinutesToFullHour(rowData.task.totalTaskTimeRegs, intl, true)}</Table.Column>,
			weekRowsColumns(rowData.task),
			<Table.Column>{rowData.task.timeRegistrationsWithNotes.length > 0 && <NotesPopOut timeRegItem={{timeRegistrationsWithNotes: rowData.task.timeRegistrationsWithNotes}} taskName={rowData.task.name} readOnly></NotesPopOut>}</Table.Column>,
			totalsColumn(rowData.task.total),
			<Table.Column></Table.Column>,
		];
	};

	const projectTimeRow = rowData => {
		return [
			<Table.Column>
				<Table.Column.Italic>No task</Table.Column.Italic>
			</Table.Column>,
			<Table.Column></Table.Column>,
			<Table.Column></Table.Column>,
			<Table.Column></Table.Column>,
			weekRowsColumns(rowData.task),
			<Table.Column>{rowData.task.timeRegistrationsWithNotes.length > 0 && <NotesPopOut timeRegItem={{timeRegistrationsWithNotes: rowData.task.timeRegistrationsWithNotes}} taskName={rowData.task.name} readOnly></NotesPopOut>}</Table.Column>,
			totalsColumn(rowData.task.total),
			<Table.Column></Table.Column>,
		];
	};

	return (
		<TableWrapper>
			<Table>
				<Table.Header>
					<Table.HeaderColumn visible flex={1} width="LARGE" />
					<Table.HeaderColumn align="left" visible={enabledColumns.projectOwner > 0} width="MEDIUM">
						<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.project_owner'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn align="center" visible={enabledColumns.workEstimate > 0} width="SMALL">
						<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.work_estimate'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn align="center" visible={enabledColumns.totalTimeReg > 0} width="SMALL">
						<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.total_time_regs'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					{weekHeaderSection()}
					<Table.HeaderColumn align="center" visible={true} width="SMALL" bordered>
						<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.notes'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn align="right" visible={true} width="SMALL" bordered background={'GREY'}>
						<Table.HeaderColumn.Title>{intl.formatMessage({id: 'common.totals'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn align="space-evenly" visible={true} width="SMALL"></Table.HeaderColumn>
				</Table.Header>
				<Table.Rows data={data} canExpand={true}>
					{({rowData, tableColumnsProps, nextLevelProps, expanded}) => {
						return (
							<>
								<Table.Row hoverStyle={{display: 'flex'}} {...tableColumnsProps} cy={isGroupedByProject ? 'project-row' : 'person-row'}>
									{isGroupedByProject ? projectRow(rowData) : personRow(rowData)}
								</Table.Row>
								{expanded && (
									<Table.Rows data={{rows: secondGroupData(rowData)}} canExpand={true} {...nextLevelProps}>
										{({rowData, tableColumnsProps, nextLevelProps, expanded}) => {
											return (
												<>
													<Table.Row hoverStyle={{display: 'flex'}} {...tableColumnsProps} cy={isGroupedByProject ? 'person-row' : 'project-row'}>
														{isGroupedByProject ? personRow(rowData) : projectRow(rowData)}
													</Table.Row>
													{expanded && (
														<Table.Rows data={{rows: baselineData(rowData)}} canExpand={false} {...nextLevelProps}>
															{({rowData, tableColumnsProps, nextLevelProps, expanded}) => {
																return rowData.task.taskId ? (
																	<Table.Row {...tableColumnsProps} cy={'task-row'}>
																		{taskTimeRow(rowData)}
																	</Table.Row>
																) : (
																	<Table.Row {...tableColumnsProps} cy={'task-row'}>
																		{projectTimeRow(rowData)}
																	</Table.Row>
																);
															}}
														</Table.Rows>
													)}
												</>
											);
										}}
									</Table.Rows>
								)}
							</>
						);
					}}
				</Table.Rows>
			</Table>
		</TableWrapper>
	);
};
export default TimeApprovalTable;
